import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { verifyUserEmail } from '../api/users';
import EmptyPoolIcon from '../assets/icons/empty-pool.svg';
import SomethingWrongPoolIcon from '../assets/icons/something-wrong-pool.svg';
import SuccessPoolIcon from '../assets/icons/success-pool.svg';
import AuthLayout from '../components/AuthLayout';
import AppButton from '../components/common/AppButton';

const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const verificationAttempted = useRef(false);

  useEffect(() => {
    // Prevent double execution in React StrictMode (development only)
    // Verification codes can only be used once, so we must prevent duplicate calls
    if (verificationAttempted.current) {
      return;
    }
    verificationAttempted.current = true;

    verifyUserEmail(searchParams)
      .then((result) => {
        if (result?.error) {
          setError(result.error);
        } else if (result?.data) {
          setSuccess(result.data);
        } else {
          setError('Unexpected response. Please try again.');
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error('Verification failed:', err);
        setError('Failed to verify email. Please try again.');
        setLoading(false);
      });
  }, [searchParams]);

  // Loading state
  if (loading) {
    return (
      <AuthLayout>
        <div className="flex flex-col items-center text-center gap-8">
          <EmptyPoolIcon />
          <div className="flex flex-col gap-4">
            <h2 className="text-2xl font-bold">Checking verification</h2>
            <p>Please wait a moment...</p>
          </div>
        </div>
      </AuthLayout>
    );
  }

  // Error state
  if (error) {
    console.error(`Error verifying email: ${error}`);
    return (
      <AuthLayout>
        <div className="flex flex-col items-center text-center gap-8">
          <SomethingWrongPoolIcon />
          <div className="flex flex-col gap-4">
            <h2 className="text-2xl font-bold">Invalid verification</h2>
            <p>
              Please request a new verification link by pressing 'Re-send email'
              in the app.
            </p>
          </div>
          <div className="w-full px-8">
            <AppButton
              fullWidth
              variant="primary"
              onClick={() => (window.location.href = '/')}
            >
              Go home
            </AppButton>
          </div>
        </div>
      </AuthLayout>
    );
  }

  // Success state
  if (success) {
    console.debug(`Success verifying email: ${success}`);
    return (
      <AuthLayout>
        <div className="flex flex-col items-center text-center gap-8">
          <SuccessPoolIcon />
          <div className="flex flex-col gap-4">
            <h2 className="text-2xl font-bold">Email Verified!</h2>
            <p>You can now access Pool Guy.</p>
          </div>
          <div className="w-full px-8">
            <AppButton
              fullWidth
              variant="primary"
              onClick={() => (window.location.href = '/onboarding/company')}
            >
              Continue
            </AppButton>
          </div>
        </div>
      </AuthLayout>
    );
  }

  // Fallback for unexpected state
  console.error('Unexpected state in VerifyEmailPage');
  return (
    <AuthLayout>
      <div className="flex flex-col items-center text-center gap-8">
        <SomethingWrongPoolIcon />
        <div className="flex flex-col gap-4">
          <h2 className="text-2xl font-bold ">Something went wrong</h2>
          <p>Please try again later.</p>
        </div>
        <div className="w-full px-8">
          <AppButton
            fullWidth
            variant="primary"
            onClick={() => (window.location.href = '/')}
          >
            Go home
          </AppButton>
        </div>
      </div>
    </AuthLayout>
  );
};

export default VerifyEmailPage;
